/**
 * StudyPickField — labeled study select used wherever a recruiter must state
 * which study an outcome applies to (StageStudyConfirmModal, the screen-fail
 * reason modal). Loads the CRM study list itself (same source as
 * PrimaryStudySelect: /api/crm/studies, source==='crm' with an adoptable id)
 * and renders internal titles (recruiter-only surface).
 */
import { useEffect, useState } from 'react';
import api from '../../services/api';
import { StudyOption, studyInternalLabel } from '../types';

interface Props {
  value: string;
  onChange: (studyId: string) => void;
  /** Highlight that this drives reporting (default true). */
  autoFocus?: boolean;
}

export default function StudyPickField({ value, onChange, autoFocus = false }: Props) {
  const [studies, setStudies] = useState<StudyOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get('/api/crm/studies')
      .then((res) => {
        if (!alive) return;
        setStudies((res.data?.studies || []).filter((s: StudyOption) => s.id && s.source === 'crm'));
      })
      .catch(() => { /* empty list — the select still allows keeping the current study */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  return (
    <>
      <label className="mt-4 block text-xs font-medium text-gray-600 dark:text-gray-300">
        Study <span className="text-rose-500">*</span>
      </label>
      <select
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={loading && studies.length === 0}
        className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
      >
        <option value="" disabled>{loading ? 'Loading studies…' : 'Select the study…'}</option>
        {studies.map((s) => (
          <option key={s.id} value={s.id!}>{studyInternalLabel(s)}</option>
        ))}
      </select>
      <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
        Reports count this lead under the study you pick — verify it if the lead
        pivoted from another study.
      </p>
    </>
  );
}
